import { Button, Flex, Input, Table, Tooltip } from 'antd';
import Domain from '../../../domain/models/Domain';
import { useCallback, useState } from 'react';
import { InputStatus } from 'antd/es/_util/statusUtils';
import { DomainEditionFormContextProps } from '../../contexts/forms/domains/DomainEditionFormContext';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';

export enum DomainElementType {
  positions = 'positions',
  networks = 'networks',
}

interface DomainElementsListProps {
  wipDomain: Domain;
  domainEditionFormContext: DomainEditionFormContextProps;
  elementType: DomainElementType;
  onElementsChange: (elements: string[]) => void;
  newElementLabel: string;
  newElementPlaceholder: string;
  elementTitle: string;
}

const DomainElementsList: React.FC<DomainElementsListProps> = ({
  wipDomain,
  domainEditionFormContext,
  elementType,
  onElementsChange,
  newElementLabel,
  elementTitle,
}) => {
  const [wipNewElement, setWipNewElement] = useState('');
  const [wipNewElementStatus, setWipNewElementStatus] =
    useState<InputStatus>('');

  const [editingElementIndex, setEditingElementIndex] = useState<
    number | null
  >(null);
  const [editingElementValue, setEditingElementValue] = useState<
    string | null
  >(null);
  const [editingElementStatus, setEditingElementStatus] =
    useState<InputStatus>('');

  const validateElement = useCallback(
    (
      value: string,
      setStatus: React.Dispatch<React.SetStateAction<InputStatus>>
    ) => {
      // No empty string
      if (value.trim() === '') {
        setStatus('error');
        return false;
      }

      // No duplicate
      if (wipDomain?.[elementType]?.includes(value)) {
        setStatus('error');
        return false;
      }

      setStatus('');
      return true;
    },
    [wipDomain]
  );

  const handleAddNewElement = useCallback(() => {
    if (!validateElement(wipNewElement, setWipNewElementStatus)) return;

    onElementsChange((wipDomain?.[elementType] || []).concat([wipNewElement]));

    setWipNewElement('');
  }, [wipDomain, wipNewElement, domainEditionFormContext, validateElement]);

  const handleEditElement = useCallback(
    (index: number, value: string) => {
      if (!validateElement(value, setEditingElementStatus)) return;


      onElementsChange(
        (wipDomain?.[elementType] || []).map((element, i) =>
          i === index ? value : element
        )
      );

      setEditingElementIndex(null);
      setEditingElementValue(null);
    },
    [wipDomain, domainEditionFormContext, validateElement]
  );

  const toggleEditElement = useCallback(
    (index: number, record: { key: string, label: string, value: string }) => {
      if (editingElementIndex === index) {
        setEditingElementIndex(null);
        setEditingElementValue(null);
      } else {
        setEditingElementIndex(index);
        setEditingElementValue(record.value);
      }
    },
    [editingElementIndex]
  );

  const handleDeleteElement = useCallback(
    (record: { key: string, label: string, value: string }, index: number) => {
      onElementsChange(
        (wipDomain?.[elementType] || []).filter(
          (element) => element !== record.value
        )
      )
      if (editingElementIndex === index) {
        setEditingElementIndex(null);
        setEditingElementValue(null);
        setEditingElementStatus('');
      }
    },
    [wipDomain, domainEditionFormContext]
  );

  return (
    <Table
      pagination={false}
      showHeader={false}
      dataSource={[
        ...(wipDomain?.[elementType] || []).sort().map((element) => ({
          key: element,
          label: element,
          value: element,
        })),
        {
          key: 'new',
          label: newElementLabel,
          value: '',
        },
      ]}
      columns={[
        {
          title: elementTitle,
          dataIndex: 'label',
          key: 'label',
          render: (_, record, index) => {
            return (
              <Flex key={index}>
                {record.key === 'new' ? (
                  <Input
                    value={wipNewElement}
                    onChange={(e) => setWipNewElement(e.target.value)}
                    onPressEnter={handleAddNewElement}
                    placeholder="Ex: Responsable des ventes"
                    status={wipNewElementStatus}
                  />
                ) : editingElementIndex === index ? (
                  <Input
                    value={editingElementValue || ''}
                    onChange={(e) => setEditingElementValue(e.target.value)}
                    onPressEnter={() => {
                      handleEditElement(index, editingElementValue || '');
                    }}
                    placeholder="Ex: Responsable des ventes"
                    status={editingElementStatus}
                  />
                ) : (
                  <span>{record.label}</span>
                )}
              </Flex>
            );
          },
        },
        {
          title: 'Action',
          dataIndex: 'action',
          key: 'action',
          render: (_, record, index) => {
            return (
              <Flex
                key={index}
                justify="end"
                align="center"
                gap="16px"
                style={{ width: '100%' }}
              >
                {record.key === 'new' ? (
                  <Tooltip title="Ajouter" zIndex={1}>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={handleAddNewElement}
                    />
                  </Tooltip>
                ) : (
                  <>
                    <Tooltip title="Modifier" zIndex={1}>
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => toggleEditElement(index, record)}
                      />
                    </Tooltip>

                    <Tooltip title="Supprimer" zIndex={1}>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteElement(record, index)}
                      />
                    </Tooltip>
                  </>
                )}
              </Flex>
            );
          },
        },
      ]}
      style={{
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
      }}
    />
  );
};

export default DomainElementsList;
